import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";
import { LANGUAGE_OPTIONS } from "../../i18n/languages";
import "./LanguageDropdown.css";

const findLanguage = (code) =>
  LANGUAGE_OPTIONS.find((lang) => lang.code === code) || LANGUAGE_OPTIONS[0];

/**
 * Reusable language picker — same option list as toolbar/mobile menus.
 * Renders the panel in a portal so it is not clipped by overflow parents.
 *
 * Optional `renderTrigger` replaces the default labeled button (e.g. selection
 * menu Translate control).
 */
const LanguageDropdown = ({
  value,
  onChange,
  ariaLabel,
  className = "",
  panelClassName = "",
  align = "end",
  renderTrigger,
}) => {
  const listId = useId();
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState({});

  const selected = findLanguage(value);

  const updatePanelPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    // Native language names (e.g. Burmese) need more than the trigger width.
    const panelWidth = Math.min(
      Math.max(rect.width, 240),
      Math.max(160, window.innerWidth - 16),
    );
    const estimatedHeight = Math.min(LANGUAGE_OPTIONS.length * 40 + 8, 280);
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const openUp = spaceBelow < estimatedHeight && rect.top > spaceBelow;

    let left = align === "start" ? rect.left : rect.right - panelWidth;
    left = Math.min(Math.max(8, left), window.innerWidth - panelWidth - 8);

    const top = openUp
      ? Math.max(8, rect.top - estimatedHeight - gap)
      : rect.bottom + gap;

    setPanelStyle({
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      width: `${panelWidth}px`,
      maxHeight: `${Math.min(estimatedHeight, window.innerHeight - 16)}px`,
      zIndex: 10050,
    });
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    updatePanelPosition();

    const handlePointerDown = (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    const handleReposition = () => updatePanelPosition();

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, align]);

  const handleSelect = (code) => {
    setIsOpen(false);
    onChange?.(code);
  };

  const toggleOpen = (event) => {
    event?.stopPropagation?.();
    setIsOpen((open) => !open);
  };

  const triggerAria = {
    "aria-label": ariaLabel,
    "aria-haspopup": "listbox",
    "aria-expanded": isOpen,
    "aria-controls": listId,
  };

  return (
    <div ref={rootRef} className={`language-dropdown ${className}`.trim()}>
      {typeof renderTrigger === "function" ? (
        renderTrigger({
          ref: triggerRef,
          isOpen,
          onClick: toggleOpen,
          ...triggerAria,
        })
      ) : (
        <button
          ref={triggerRef}
          type="button"
          className={`language-dropdown-trigger${isOpen ? " is-open" : ""}`}
          onClick={toggleOpen}
          {...triggerAria}
        >
          <span className="language-dropdown-label">{selected.label}</span>
          <FaChevronDown aria-hidden="true" />
        </button>
      )}

      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            id={listId}
            className={`language-dropdown-panel ${panelClassName}`.trim()}
            style={panelStyle}
            role="listbox"
            aria-label={ariaLabel}
          >
            {LANGUAGE_OPTIONS.map((lang) => {
              const isActive = lang.code === selected.code;
              return (
                <button
                  key={lang.code}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`language-dropdown-option${
                    isActive ? " is-active" : ""
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSelect(lang.code);
                  }}
                >
                  <span>{lang.label}</span>
                  {isActive && (
                    <span className="language-dropdown-check" aria-hidden="true">
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default LanguageDropdown;
